import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Boxes, ShieldCheck, Truck, Leaf, FileText, Recycle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const stat = (v) => (v === undefined || v === null ? "—" : v);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [assets, setAssets] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/dashboard/stats"), api.get("/assets")])
      .then(([s, a]) => { setStats(s.data); setAssets(a.data); })
      .catch(() => {});
  }, []);

  const byStatus = ["Received", "Pending Audit", "In Transit", "Data Destroyed", "Recycled", "Resold"].map((k) => ({
    name: k,
    value: assets.filter((a) => a.status === k).length,
  }));

  const cards = [
    { label: "Total Assets", value: stat(stats?.total_assets), icon: Boxes },
    { label: "Data Destroyed", value: stat(stats?.data_destroyed), icon: ShieldCheck },
    { label: "In Transit", value: stat(stats?.in_transit), icon: Truck },
    { label: "Processed", value: stat(stats?.processed), icon: Recycle },
    { label: "Quote Requests", value: stat(stats?.quotes), icon: FileText },
    { label: "CO₂e Saved (t)", value: stat(stats?.co2_saved_tonnes), icon: Leaf, accent: true },
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary mb-2">/ Overview</p>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">Welcome back, {user?.name?.split(" ")[0]}.</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 border-t border-l border-border mb-8">
        {cards.map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            className={`border-r border-b border-border p-6 group hover:-translate-y-0.5 transition-transform ${c.accent ? "bg-secondary/10" : ""}`}
          >
            <div className="flex items-center justify-between mb-6">
              <c.icon size={20} className={c.accent ? "text-secondary" : "text-primary"} />
            </div>
            <div className="font-mono font-semibold text-3xl md:text-4xl tracking-tight">{c.value}</div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{c.label}</div>
          </motion.div>
        ))}
      </div>

      <div className="border border-border p-6">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">Asset lifecycle distribution</div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={byStatus} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fill: "#a3a3a3", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#262626" }} tickLine={false} interval={0} />
              <YAxis tick={{ fill: "#a3a3a3", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#171717" }} contentStyle={{ background: "#0a0a0a", border: "1px solid #262626", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }} />
              <Bar dataKey="value" radius={0}>
                {byStatus.map((e, i) => (
                  <Cell key={i} fill={["Recycled", "Resold", "Data Destroyed"].includes(e.name) ? "#4fae7d" : "#c87e4a"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
